import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Eye, EyeOff, Loader2, Gift } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

export default function SignupPage() {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    location: 'Ottignies-Louvain-la-Neuve',
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    const result = await register(form);
    setLoading(false);

    if (result.success) {
      // Set flag for welcome modal
      localStorage.setItem('okni_new_user', 'true');
      toast.success('Bienvenue sur Oknî ! Vous avez reçu 10 crédits de bienvenue.');
      navigate('/');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Welcome Banner */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2">Rejoindre Oknî</h1>
            <p className="text-muted-foreground">
              Créez votre compte et commencez à échanger
            </p>
          </div>

          {/* Bonus Banner */}
          <Card className="mb-6 border-secondary/50 bg-secondary/5">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                <Gift className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-secondary-foreground">10 crédits offerts</p>
                <p className="text-sm text-muted-foreground">
                  Recevez 10 crédits de bienvenue à l'inscription
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Form */}
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Nom complet *</Label>
                  <Input
                    id="full_name"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                    className="rounded-xl"
                    data-testid="signup-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jean@exemple.be"
                    required
                    className="rounded-xl"
                    data-testid="signup-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      required
                      minLength={6}
                      className="rounded-xl pr-10"
                      data-testid="signup-password-input"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Localisation *</Label>
                  <Input
                    id="location"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    placeholder="Ottignies-Louvain-la-Neuve"
                    required
                    className="rounded-xl"
                    data-testid="signup-location-input"
                  />
                </div>

                {/* Consent Checkbox */}
                <label className="flex items-start gap-3 p-3 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted/70 transition-colors">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-primary rounded"
                    data-testid="signup-terms-checkbox"
                  />
                  <span className="text-sm text-muted-foreground">
                    En m&apos;inscrivant, j&apos;accepte les{' '}
                    <Link to="/terms" className="text-primary hover:underline" target="_blank">
                      Conditions Générales d&apos;Utilisation
                    </Link>
                    {' '}et la{' '}
                    <Link to="/privacy" className="text-primary hover:underline" target="_blank">
                      Politique de Confidentialité
                    </Link>.
                  </span>
                </label>

                <Button
                  type="submit"
                  className="w-full btn-primary mt-6"
                  disabled={loading || !acceptedTerms}
                  data-testid="signup-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Création du compte...
                    </>
                  ) : (
                    "S'inscrire"
                  )}
                </Button>
              </form>
              
              {/* Google OAuth */}
              <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Ou s'inscrire avec</span>
                  </div>
                </div>
                
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => {
                    const redirectUrl = window.location.origin + '/auth/callback';
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                >
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuer avec Google
                </Button>
              </div>

              <p className="text-center text-sm text-muted-foreground mt-6">
                En vous inscrivant, vous acceptez nos{' '}
                <Link to="/terms" className="text-primary hover:underline">
                  conditions d'utilisation
                </Link>{' '}
                et notre{' '}
                <Link to="/privacy" className="text-primary hover:underline">
                  politique de confidentialité
                </Link>
                .
              </p>
            </CardContent>
          </Card>

          <p className="text-center mt-6 text-muted-foreground">
            Déjà membre ?{' '}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </Layout>
  );
}
